import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Plus, Trash2 } from 'lucide-react';
import Reveal from '../components/Reveal';
import InquiryForm from '../components/InquiryForm';
import Loader from '../components/Loader';
import useAsync from '../hooks/useAsync';
import { getContent } from '../lib/content';
import { formatINR } from '../lib/whatsapp';
import { useSettings } from '../context/SettingsContext';

export default function Customize() {
  const [params] = useSearchParams();
  const { showPricing } = useSettings();
  const { data, loading } = useAsync(getContent, []);
  const packages = useMemo(() => data?.packages || [], [data]);
  const allAddOns = data?.addOns || [];

  const [pkgId, setPkgId] = useState(params.get('pkg') || '');
  const [selected, setSelected] = useState(
    () => (params.get('addons') || '').split(',').filter(Boolean)
  );

  // Default to the first package once content loads.
  useEffect(() => {
    if (!pkgId && packages.length) setPkgId(packages[0].id);
  }, [packages, pkgId]);

  const pkg = useMemo(() => packages.find((p) => p.id === pkgId), [packages, pkgId]);
  const chosenAddOns = allAddOns.filter((a) => selected.includes(a.id));
  const total = (pkg?.price || 0) + chosenAddOns.reduce((s, a) => s + a.price, 0);

  if (loading) return <Loader label="Loading…" />;

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="section py-12">
      <Reveal className="mb-10 text-center">
        <h1 className="font-display text-4xl font-extrabold">Build Your <span className="text-gradient">Event</span></h1>
        <p className="mt-3 text-ink-300">Pick a package, add the extras you love, and send your inquiry.</p>
      </Reveal>

      <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
        {/* Left: selection */}
        <div className="space-y-8">
          {/* Package picker */}
          <section>
            <h2 className="mb-4 font-display text-lg font-semibold">1. Choose a package</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {packages.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPkgId(p.id)}
                  className={`flex items-center gap-3 rounded-xl border p-3 text-left transition ${
                    pkgId === p.id ? 'border-brand bg-brand/10' : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <img src={p.cover} alt={p.title} className="h-14 w-14 rounded-lg object-cover" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">{p.title}</p>
                    {showPricing && <p className="text-xs text-ink-300">{formatINR(p.price)}</p>}
                  </div>
                  {pkgId === p.id && <Check size={18} className="ml-auto text-brand" />}
                </button>
              ))}
            </div>
          </section>

          {/* Add-ons */}
          <section>
            <h2 className="mb-4 font-display text-lg font-semibold">2. Add-on services</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {allAddOns.map((a) => {
                const on = selected.includes(a.id);
                return (
                  <button
                    key={a.id}
                    onClick={() => toggle(a.id)}
                    className={`flex items-center justify-between rounded-lg border px-4 py-3 text-sm transition ${
                      on ? 'border-brand bg-brand/10 text-white' : 'border-white/10 text-ink-200 hover:border-white/30'
                    }`}
                  >
                    <span>{a.title}</span>
                    <span className="flex items-center gap-2 text-ink-300">
                      {showPricing && formatINR(a.price)}
                      <span className={`grid h-5 w-5 place-items-center rounded-full ${on ? 'bg-brand text-white' : 'border border-white/20'}`}>
                        {on ? <Check size={12} /> : <Plus size={12} />}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        {/* Right: sticky summary + form */}
        <div className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <div className="glass p-6">
            <h2 className="mb-4 font-display text-lg font-semibold">Summary</h2>
            {pkg && (
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <span className="text-sm text-white">{pkg.title}</span>
                {showPricing && <span className="text-sm text-ink-200">{formatINR(pkg.price)}</span>}
              </div>
            )}
            <AnimatePresence initial={false}>
              {chosenAddOns.map((a) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center justify-between py-2 text-sm text-ink-300"
                >
                  <span className="flex items-center gap-2">
                    <button onClick={() => toggle(a.id)} className="text-danger/70 hover:text-danger">
                      <Trash2 size={13} />
                    </button>
                    {a.title}
                  </span>
                  {showPricing && <span>{formatINR(a.price)}</span>}
                </motion.div>
              ))}
            </AnimatePresence>
            <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-4">
              <span className="font-display font-semibold text-white">Estimated Total</span>
              {showPricing ? (
                <span className="font-display text-xl font-bold text-gradient">{formatINR(total)}</span>
              ) : (
                <span className="font-display text-sm font-semibold text-ink-200">Price on request</span>
              )}
            </div>
          </div>

          <div className="glass p-6">
            <h2 className="mb-4 font-display text-lg font-semibold">3. Your details</h2>
            <InquiryForm pkg={pkg} addOns={chosenAddOns} origin="customize" compact />
          </div>
        </div>
      </div>
    </div>
  );
}
